import { supabase } from './supabase';
import { trackEvent } from './analytics';

/**
 * Anonymous checkout → signed-in session, without the magic-link detour.
 *
 * The Stripe success redirect carries the Checkout Session id. The server
 * verifies it (anonymous, complete, fresh, account created for this
 * purchase — see /api/stripe/claim-anonymous-checkout), exchanges a
 * one-time token itself and returns the session tokens, which we install
 * with supabase.auth.setSession. On any refusal the caller falls back to
 * the existing "send me a magic link" modal, so nothing that worked before
 * gets worse.
 *
 * Browser binding: a claim is only attempted for a session id this browser
 * itself opened (remembered in localStorage when Checkout was launched, see
 * useCheckoutNavigation). A success URL forwarded to someone else therefore
 * never signs them into the buyer's account — a classic login-CSRF hole
 * otherwise. localStorage rather than sessionStorage because the
 * "keep this tab open" flow finishes Checkout in a second tab.
 *
 * settleClaimedAccount: the account may have been opened under an email the
 * buyer does not control. Every sign-in of an anonymous-checkout account
 * asks the server to settle; the server only acts when the session came
 * through an inbox-proving method and is not the claim session itself.
 */
export type AnonymousClaimResult = 'signed_in' | 'fallback';

const OPENED_KEY = 'la_anon_checkout_opened';
const OPENED_TTL_MS = 24 * 3600_000;
const SESSION_ID_RE = /cs_(live|test)_[A-Za-z0-9]{10,}/;
// Past this the buyer is shown the magic-link modal instead of a blank wait.
const CLAIM_TIMEOUT_MS = 10_000;

/** Remember the Checkout Session this browser is about to open (parsed from Stripe's URL). */
export function rememberOpenedCheckout(checkoutUrl: string): void {
  const id = checkoutUrl.match(SESSION_ID_RE)?.[0];
  if (!id) return;
  try {
    localStorage.setItem(OPENED_KEY, JSON.stringify({ id, at: Date.now() }));
  } catch { /* no storage → no auto sign-in, the magic-link modal still works */ }
}

function openedHere(sessionId: string): boolean {
  try {
    const raw = localStorage.getItem(OPENED_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { id?: unknown; at?: unknown };
    return parsed.id === sessionId
      && typeof parsed.at === 'number'
      && Date.now() - parsed.at < OPENED_TTL_MS;
  } catch {
    return false;
  }
}

function forgetOpenedCheckout(): void {
  try { localStorage.removeItem(OPENED_KEY); } catch { /* best effort */ }
}

export function decodeSessionId(accessToken: string | undefined | null): string | null {
  if (!accessToken) return null;
  try {
    const part = accessToken.split('.')[1] || '';
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { session_id?: unknown };
    return typeof payload.session_id === 'string' ? payload.session_id : null;
  } catch {
    return null;
  }
}

export async function claimAnonymousCheckout(sessionId: string): Promise<AnonymousClaimResult> {
  if (!/^cs_(live|test)_[A-Za-z0-9]{10,}$/.test(sessionId)) return 'fallback';
  if (!openedHere(sessionId)) {
    trackEvent('checkout_anon_autologin', { result: 'rejected', reason: 'not_opened_here' });
    return 'fallback';
  }
  // One deadline for the whole sequence (fetch, getSession, setSession):
  // whatever is still pending when it fires, the caller shows the modal.
  const abort = new AbortController();
  let timer = 0;
  let expired = false;
  const deadline = new Promise<AnonymousClaimResult>((resolve) => {
    timer = window.setTimeout(() => {
      expired = true;
      abort.abort();
      trackEvent('checkout_anon_autologin', { result: 'error', reason: 'timeout' });
      resolve('fallback');
    }, CLAIM_TIMEOUT_MS);
  });
  try {
    return await Promise.race([deadline, claimSteps(sessionId, abort.signal, () => expired)]);
  } finally {
    window.clearTimeout(timer);
  }
}

async function claimSteps(sessionId: string, signal: AbortSignal, hasExpired: () => boolean): Promise<AnonymousClaimResult> {
  try {
    const response = await fetch('/api/stripe/claim-anonymous-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
      signal,
    });
    if (!response.ok) {
      let code = String(response.status);
      try {
        const body = await response.json() as { code?: string };
        if (body?.code) code = body.code;
      } catch { /* status is enough */ }
      trackEvent('checkout_anon_autologin', { result: 'rejected', reason: code });
      return 'fallback';
    }
    const data = await response.json() as { access_token?: string; refresh_token?: string };
    if (!data?.access_token || !data.refresh_token) {
      trackEvent('checkout_anon_autologin', { result: 'rejected', reason: 'no_token' });
      return 'fallback';
    }
    // A manual sign-in that completes anywhere from here on must win, not
    // be replaced: watch for it BEFORE taking the getSession snapshot, and
    // through setSession's own /user round-trip.
    let concurrent: { access_token: string; refresh_token: string; user: { id: string } } | null = null;
    const { data: watcher } = supabase.auth.onAuthStateChange((event, session) => {
      // PASSWORD_RECOVERY is what a recovery-link sign-in emits instead of SIGNED_IN.
      if ((event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') && session && session.access_token !== data.access_token) {
        concurrent = { access_token: session.access_token, refresh_token: session.refresh_token, user: { id: session.user.id } };
      }
    });
    let installed: { session: { user: { id: string } } | null } | null = null;
    let error: { message?: string } | null = null;
    try {
      const { data: { session: existing } } = await supabase.auth.getSession();
      if (existing || concurrent) {
        trackEvent('checkout_anon_autologin', { result: 'rejected', reason: 'already_signed_in' });
        return 'fallback';
      }
      if (hasExpired()) return 'fallback';
      const result = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      installed = result.data;
      error = result.error;
    } finally {
      watcher.subscription.unsubscribe();
    }
    const other = concurrent as { access_token: string; refresh_token: string; user: { id: string } } | null;
    if (error || !installed?.session) {
      // A failed install can wipe the stored session (session_not_found →
      // SIGNED_OUT); put back whatever signed in meanwhile.
      if (other) await supabase.auth.setSession({ access_token: other.access_token, refresh_token: other.refresh_token }).catch(() => undefined);
      trackEvent('checkout_anon_autologin', { result: 'verify_failed', reason: error?.message?.slice(0, 60) || 'no_session' });
      return 'fallback';
    }
    if (other) {
      // Whatever signed in meanwhile (another account, or a verified session
      // of this very account) is the one to keep. If it cannot be restored,
      // leave NO session rather than the wrong one — the modal then shows.
      const restored = await supabase.auth.setSession({ access_token: other.access_token, refresh_token: other.refresh_token }).catch(() => null);
      if (!restored || restored.error || !restored.data.session) {
        await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
      }
      trackEvent('checkout_anon_autologin', { result: 'rejected', reason: 'concurrent_sign_in' });
      return 'fallback';
    }
    // If the deadline already fired, the caller has shown the modal; the
    // editor closes it as soon as a user appears (EditorPage), so a late
    // but successful install is still a sign-in.
    forgetOpenedCheckout();
    trackEvent('checkout_anon_autologin', { result: hasExpired() ? 'signed_in_late' : 'signed_in' });
    return 'signed_in';
  } catch (error) {
    if (signal.aborted) return 'fallback'; // the deadline already reported
    trackEvent('checkout_anon_autologin', { result: 'error', reason: error instanceof Error ? error.message.slice(0, 60) : 'unknown' });
    return 'fallback';
  }
}

const settledSessions = new Set<string>();
const pendingSettlements = new Map<string, Promise<boolean>>();

type SettleSession = { access_token: string; user: { app_metadata?: Record<string, unknown> | null } };

function runSettlement(session: SettleSession, current: string): Promise<boolean> {
  const request = fetch('/api/auth/settle-claimed-account', {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
  }).then(async (r) => {
    trackEvent('checkout_claim_settled', { ok: r.ok ? 1 : 0 });
    if (!r.ok) return false;
    const body = await r.json().catch(() => ({})) as { settled?: boolean };
    // Only a completed settlement is final for this session. "Nothing to
    // do" is not cached: a claim row may still appear after this answer
    // (the guard-read/INSERT window), and the next auth event must ask again.
    if (body.settled === true) settledSessions.add(current);
    return true;
  }).catch(() => false).finally(() => {
    pendingSettlements.delete(current);
  });
  pendingSettlements.set(current, request);
  return request;
}

/**
 * Called by the auth store on SIGNED_IN / INITIAL_SESSION / TOKEN_REFRESHED.
 * No-op unless the account was created by anonymous checkout; the server
 * decides the rest (and is idempotent per session, so calling again is
 * harmless — one small request per auth event). Only a completed
 * settlement marks this session as done here; anything else is asked again
 * on the next auth event.
 */
export function settleClaimedAccount(session: SettleSession): void {
  if (session.user.app_metadata?.created_via !== 'anonymous_checkout') return;
  const current = decodeSessionId(session.access_token);
  if (!current || settledSessions.has(current) || pendingSettlements.has(current)) return;
  void runSettlement(session, current);
}

/**
 * Flows that set a password themselves (password recovery) call this before
 * saving, so settlement's random reset can never land after the user's own:
 * waits for every in-flight settlement and, if this session's attempt
 * failed, retries it once. Returns false when settlement is still not done —
 * the caller must then refuse to save (a later retry would overwrite it).
 * Accounts that never went through anonymous checkout resolve true at once.
 */
export async function ensureSettled(session: SettleSession | null): Promise<boolean> {
  if (!session || session.user.app_metadata?.created_via !== 'anonymous_checkout') return true;
  const current = decodeSessionId(session.access_token);
  if (!current) return true;
  // Drain every in-flight settlement, including ones started while waiting.
  while (pendingSettlements.size > 0) {
    await Promise.all(Array.from(pendingSettlements.values()));
  }
  if (settledSessions.has(current)) return true;
  // Ask the server now (a 2xx means: settled, or nothing open to settle).
  const ok = await runSettlement(session, current);
  // Anything that started meanwhile must also be finished before saving.
  while (pendingSettlements.size > 0) {
    await Promise.all(Array.from(pendingSettlements.values()));
  }
  return ok;
}
